export const MONTH_NAMES = [
  'Január',
  'Február',
  'Március',
  'Április',
  'Május',
  'Június',
  'Július',
  'Augusztus',
  'Szeptember',
  'Október',
  'November',
  'December',
];

export const DAY_HEADERS_SHORT = ['H', 'K', 'Sz', 'Cs', 'P', 'Sz', 'V'];

export const DAY_NAMES_FULL = [
  'Hétfő',
  'Kedd',
  'Szerda',
  'Csütörtök',
  'Péntek',
  'Szombat',
  'Vasárnap',
];

export const DAY_ABBREV = ['VAS', 'HÉT', 'KED', 'SZE', 'CSÜ', 'PÉN', 'SZO'];

export const FORTNIGHT_DAYS = 14;

const MEAL_TITLE_PATTERN = /reggeli|ebéd|vacsora|uzsonna|étkezés|etkezes/i;

export const EVENT_DISPLAY_TAGS = {
  health: { id: 'health', label: 'Egészség', color: '#4CAF50' },
  meal: { id: 'meal', label: 'Étkezés', color: '#FF9800' },
  program: { id: 'program', label: 'Program', color: '#9C27B0' },
  service: { id: 'service', label: 'Szolgáltatás', color: '#2196F3' },
};

/** Canonical 8 types for admin + public display */
export const ADMIN_EVENT_TYPES = [
  { id: 'meal', name: 'Étkezés', icon: '🍽️', color: '#FF9800' },
  { id: 'health', name: 'Egészség', icon: '❤️', color: '#4CAF50' },
  { id: 'exercise', name: 'Mozgás', icon: '🏃', color: '#8BC34A' },
  { id: 'cultural', name: 'Kulturális', icon: '🎭', color: '#9C27B0' },
  { id: 'social', name: 'Közösségi', icon: '👥', color: '#2196F3' },
  { id: 'service', name: 'Szolgáltatás', icon: '🔧', color: '#607D8B' },
  { id: 'therapy', name: 'Terápia', icon: '🌿', color: '#009688' },
  { id: 'other', name: 'Egyéb', icon: '•', color: '#9E9E9E' },
];

export const EVENT_TYPES = {
  meal: { name: 'Étkezés', icon: '🍽️', color: '#FF9800' },
  health: { name: 'Egészség', icon: '❤️', color: '#4CAF50' },
  exercise: { name: 'Mozgás', icon: '🏃', color: '#8BC34A' },
  cultural: { name: 'Kulturális', icon: '🎭', color: '#9C27B0' },
  social: { name: 'Közösségi', icon: '👥', color: '#2196F3' },
  service: { name: 'Szolgáltatás', icon: '🔧', color: '#607D8B' },
  therapy: { name: 'Terápia', icon: '🌿', color: '#009688' },
  other: { name: 'Egyéb', icon: '•', color: '#9E9E9E' },
  activity: { name: 'Tevékenység', icon: '🎯', color: '#FF9800' },
  entertainment: { name: 'Szórakozás', icon: '🎮', color: '#E91E63' },
  outdoor: { name: 'Szabadtéri', icon: '🌳', color: '#4CAF50' },
  celebration: { name: 'Ünnep', icon: '🎉', color: '#FF5722' },
  meeting: { name: 'Összejövetel', icon: '📅', color: '#2196F3' },
};

export function getDaysInMonth(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = (firstDay.getDay() + 6) % 7;

  const days = [];
  for (let i = 0; i < startingDay; i += 1) {
    days.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    days.push(day);
  }
  return days;
}

export function formatTime(time) {
  if (!time) return '';
  const [h, m] = time.split(':');
  return `${h}:${m}`;
}

export function calculateEndTime(startTime, durationMinutes = 60) {
  if (!startTime) return '';
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + (durationMinutes || 60);
  const endHours = Math.floor(totalMinutes / 60);
  const endMinutes = totalMinutes % 60;
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
}

/** Minutes since midnight; unpadded "9:30" sorts before "18:00". Missing time → end of day. */
export function parseTimeToMinutes(time) {
  if (!time || typeof time !== 'string') return Number.POSITIVE_INFINITY;
  const parts = time.trim().split(':').map((p) => parseInt(p, 10));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return Number.POSITIVE_INFINITY;
  const [hours, minutes] = parts;
  return hours * 60 + minutes;
}

export function compareEventsByTime(a, b) {
  const diff = parseTimeToMinutes(a?.time) - parseTimeToMinutes(b?.time);
  if (diff !== 0) return diff;
  return (a?.title || '').localeCompare(b?.title || '', 'hu');
}

export function flattenEventsByDate(eventsByDate) {
  const result = [];
  Object.entries(eventsByDate || {}).forEach(([date, dayEvents]) => {
    (dayEvents || []).forEach((event) => {
      result.push({ ...event, date });
    });
  });
  return result;
}

export function mergeEventsByDate(...maps) {
  const merged = {};
  maps.forEach((map) => {
    Object.entries(map || {}).forEach(([date, dayEvents]) => {
      if (!merged[date]) merged[date] = [];
      merged[date] = [...merged[date], ...(dayEvents || [])];
    });
  });
  return merged;
}

export function eventsByDayForMonth(eventsByDate, viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth() + 1;
  const prefix = `${year}-${String(month).padStart(2, '0')}-`;
  const byDay = {};

  Object.entries(eventsByDate || {}).forEach(([dateStr, dayEvents]) => {
    if (!dateStr.startsWith(prefix)) return;
    const day = parseInt(dateStr.split('-')[2], 10);
    if (Number.isFinite(day)) byDay[day] = dayEvents;
  });

  return byDay;
}

export function getUpcomingEvents(flatEvents, limit = 3) {
  const now = new Date();
  return flatEvents
    .filter((event) => {
      if (event.status === 'cancelled') return false;
      const start = new Date(`${event.date}T${event.time || '00:00'}`);
      return start >= now;
    })
    .sort((a, b) => {
      const da = new Date(`${a.date}T${a.time || '00:00'}`);
      const db = new Date(`${b.date}T${b.time || '00:00'}`);
      return da - db;
    })
    .slice(0, limit);
}

export function isToday(day, viewDate) {
  if (!day) return false;
  const today = new Date();
  return (
    day === today.getDate() &&
    viewDate.getMonth() === today.getMonth() &&
    viewDate.getFullYear() === today.getFullYear()
  );
}

export function dayAbbrevForDate(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return DAY_ABBREV[d.getDay()] || '';
}

export async function fetchMonthEvents(apiBase, year, month) {
  const res = await fetch(`${apiBase}/events/calendar/${year}/${month}`);
  if (!res.ok) return {};
  const data = await res.json();
  return data.events || {};
}

export function getMonday(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

export function addDays(date, days) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

export function buildDateRange(startDate, count) {
  const days = [];
  for (let i = 0; i < count; i += 1) {
    days.push(addDays(startDate, i));
  }
  return days;
}

export function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isSameCalendarDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function formatPeriodLabel(startDate, endDate) {
  const fmt = new Intl.DateTimeFormat('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });
  const start = fmt.format(startDate);
  const endMonthDay = new Intl.DateTimeFormat('hu-HU', { month: 'long', day: 'numeric' }).format(endDate);
  if (startDate.getFullYear() === endDate.getFullYear() && startDate.getMonth() === endDate.getMonth()) {
    const startDay = startDate.getDate();
    return `${startDate.getFullYear()}. ${MONTH_NAMES[startDate.getMonth()]} ${startDay}. – ${endDate.getDate()}.`;
  }
  return `${start} – ${endMonthDay}`;
}

export function getEventDisplayTag(event) {
  const type = event?.type;
  if (type === 'meal' || (type === 'activity' && MEAL_TITLE_PATTERN.test(event?.title || ''))) {
    return EVENT_DISPLAY_TAGS.meal;
  }
  if (type === 'health') return EVENT_DISPLAY_TAGS.health;
  if (['service', 'meeting', 'therapy'].includes(type)) return EVENT_DISPLAY_TAGS.service;
  if (['cultural', 'entertainment', 'celebration', 'outdoor', 'exercise', 'social', 'other', 'activity'].includes(type)) {
    return EVENT_DISPLAY_TAGS.program;
  }
  const title = (event?.title || '').toLowerCase();
  if (MEAL_TITLE_PATTERN.test(title)) return EVENT_DISPLAY_TAGS.meal;
  return EVENT_DISPLAY_TAGS.program;
}

export function getEventTypeMeta(event) {
  return EVENT_TYPES[event?.type] || EVENT_TYPES.cultural;
}

export function getEventsForDay(eventsByDate, dateKey) {
  const list = (eventsByDate && eventsByDate[dateKey]) || [];
  return [...list]
    .filter((e) => e.status !== 'cancelled')
    .sort(compareEventsByTime);
}

export async function fetchEventsForDateRange(apiBase, startDate, dayCount = FORTNIGHT_DAYS) {
  const days = buildDateRange(startDate, dayCount);
  const monthKeys = new Set();
  days.forEach((d) => {
    monthKeys.add(`${d.getFullYear()}-${d.getMonth() + 1}`);
  });

  const fetches = [...monthKeys].map((key) => {
    const [y, m] = key.split('-').map(Number);
    return fetchMonthEvents(apiBase, y, m);
  });

  const maps = await Promise.all(fetches);
  return mergeEventsByDate(...maps);
}

export async function fetchEventsForMonthView(apiBase, viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth() + 1;
  const next = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
  const maps = await Promise.all([
    fetchMonthEvents(apiBase, year, month),
    fetchMonthEvents(apiBase, next.getFullYear(), next.getMonth() + 1),
  ]);
  return mergeEventsByDate(...maps);
}

export function formatDayHeading(date) {
  const dow = DAY_NAMES_FULL[(date.getDay() + 6) % 7] || '';
  const month = MONTH_NAMES[date.getMonth()].toLowerCase();
  return `${date.getFullYear()}. ${month} ${date.getDate()}., ${dow}`;
}

export function formatDayHeadingFromDateStr(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return formatDayHeading(new Date(y, m - 1, d));
}

export function getEventTypeById(typeId) {
  const fromAdmin = ADMIN_EVENT_TYPES.find((t) => t.id === typeId);
  if (fromAdmin) return fromAdmin;
  const legacy = EVENT_TYPES[typeId];
  if (legacy) {
    return { id: typeId, name: legacy.name, icon: legacy.icon, color: legacy.color };
  }
  return ADMIN_EVENT_TYPES.find((t) => t.id === 'other') || ADMIN_EVENT_TYPES[7];
}
